import {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from '../../../../constants/queryKeys';
import { CommunicationRecordTargetType } from '../../../../globalModels/common/communicationRecordTargetType.ts';

type ActivityInformationContextType = {
  setActivityId: Dispatch<SetStateAction<number | undefined>>;
  activityId: number | undefined;
  syncFooterWithMainActivity: () => Promise<void>;
};

export const ActivityInformationContext = createContext<ActivityInformationContextType | null>(null);
ActivityInformationContext.displayName = 'ActivityInformationContext';

export function ActivityInformationContextProvider(props: PropsWithChildren) {
  const [activityId, setActivityId] = useState<number | undefined>(undefined);
  const queryClient = useQueryClient();
  const previousActivityIdRef = useRef<number | undefined>(undefined);

  // Comprehensive sync function that refreshes all activity-related queries
  const syncFooterWithMainActivity = useCallback(async () => {
    if (!activityId) return;

    // Invalidate all activity-related queries to ensure footer stays in sync
    await Promise.all([
      // Main activity queries (used by edit forms and main page)
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.editActivity(activityId)],
        type: 'all'
      }),

      // Activity model queries (used by main page displays)
      queryClient.invalidateQueries({
        queryKey: ['activity', activityId],
        type: 'all'
      }),

      // Activity follow-ups
      queryClient.invalidateQueries({
        queryKey: ['activityFollowUps', activityId],
        type: 'all'
      }),

      // Activity information displays
      queryClient.invalidateQueries({
        queryKey: ['entityCommunicationRecords', CommunicationRecordTargetType.Activity, activityId],
        type: 'all'
      })
    ]);
  }, [activityId, queryClient]);

  // Auto-sync when activityId changes (triggered by EditSingleActivity)
  useEffect(() => {
    if (activityId !== previousActivityIdRef.current) {
      const previousId = previousActivityIdRef.current;
      previousActivityIdRef.current = activityId;

      // Clean up previous activity queries
      if (previousId && previousId !== activityId) {
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.editActivity(previousId)],
          type: 'all'
        });
      }

      // Auto-sync new activity
      if (activityId) {
        // Small delay to allow main activity data to load first
        setTimeout(() => {
          syncFooterWithMainActivity();
        }, 100);
      }
    }
  }, [activityId, syncFooterWithMainActivity, queryClient]);

  return (
    <ActivityInformationContext.Provider
      value={{
        activityId,
        setActivityId,
        syncFooterWithMainActivity
      }}>
      {props.children}
    </ActivityInformationContext.Provider>
  );
}
